import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CompanyResponse, StripeConnectStatus, CompanyServiceOrderResponse } from '../../../../@shared/models/response.module';
import { PaymentService } from '../../../../@shared/services/payment.service';
import { EndpointService } from '../../../../@shared/endpoints.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CreateInvoiceModal } from './components/create-invoice-modal/create-invoice-modal';
import { ViewInvoiceModal } from './components/view-invoice-modal/view-invoice-modal';
import { FeeAcceptanceModal } from './components/fee-acceptance-modal/fee-acceptance-modal';
import { ToastService } from '../../../../@core/services/toast.service';
import { CompanyService } from '../../../../@shared/services/company.service';

@Component({
  selector: 'app-company-profile-billing',
  standalone: false,
  templateUrl: './company-profile-billing.html',
  styleUrl: './company-profile-billing.css'
})
export class CompanyProfileBilling implements OnInit {
  @Input() company: CompanyResponse;
  @Input() userRole: number;
  @Output() companyUpdated = new EventEmitter<CompanyResponse>();

  connectStatus: StripeConnectStatus | null = null;
  loading: boolean = true;
  actionLoading: boolean = false;
  error: string | null = null;
  selectedBusinessType: 'individual' | 'company' = 'individual';

  // Service orders
  orders: CompanyServiceOrderResponse[] = [];
  loadingOrders: boolean = false;
  ordersStatusFilter: string = '';

  // Custom payment settings
  customPaymentEnabled: boolean = false;
  customPaymentMethodName: string = '';
  customPaymentInstructions: string = '';
  savingCustomPayment: boolean = false;

  // Currency settings
  selectedCurrency: string = 'NOK';
  savingCurrency: boolean = false;
  currencyOptions = [
    { code: 'NOK', name: 'Norwegian Krone (NOK)' },
    { code: 'SEK', name: 'Swedish Krona (SEK)' },
    { code: 'EUR', name: 'Euro (EUR)' },
    { code: 'USD', name: 'US Dollar (USD)' },
    { code: 'GBP', name: 'British Pound (GBP)' },
    { code: 'DKK', name: 'Danish Krone (DKK)' },
  ];

  constructor(
    private paymentService: PaymentService,
    private endpointService: EndpointService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService,
    private companyService: CompanyService
  ) {}

  ngOnInit() {
    this.loadConnectStatus();
    this.loadOrders();
    this.loadCustomPaymentSettings();
  }

  loadCustomPaymentSettings() {
    // Load from company data
    this.customPaymentEnabled = this.company.custom_payment_enabled || false;
    this.customPaymentMethodName = this.company.custom_payment_method_name || '';
    this.customPaymentInstructions = this.company.custom_payment_instructions || '';
    this.selectedCurrency = this.company.default_currency || 'NOK';
  }

  saveCurrency() {
    this.savingCurrency = true;

    this.companyService.editCompany({
      default_currency: this.selectedCurrency
    } as any).subscribe({
      next: (updatedCompany: CompanyResponse) => {
        this.toastService.success(this.translateService.instant('companyProfile.billing.currencySaved'));
        this.savingCurrency = false;
        this.company = { ...this.company, ...updatedCompany };
        this.companyUpdated.emit(this.company);
      },
      error: () => {
        this.toastService.error(this.translateService.instant('companyProfile.billing.currencySaveError'));
        this.savingCurrency = false;
      }
    });
  }

  saveCustomPaymentSettings() {
    this.savingCustomPayment = true;

    this.companyService.editCompany({
      custom_payment_enabled: this.customPaymentEnabled,
      custom_payment_method_name: this.customPaymentMethodName,
      custom_payment_instructions: this.customPaymentInstructions
    } as any).subscribe({
      next: (updatedCompany: CompanyResponse) => {
        this.toastService.success(this.translateService.instant('companyProfile.billing.customPaymentSaved'));
        this.savingCustomPayment = false;
        // Update company object and emit
        this.company = { ...this.company, ...updatedCompany };
        this.companyUpdated.emit(this.company);
      },
      error: () => {
        this.toastService.error(this.translateService.instant('companyProfile.billing.customPaymentSaveError'));
        this.savingCustomPayment = false;
      }
    });
  }

  isStripeConnected(): boolean {
    return this.connectStatus?.status === 'active' && this.connectStatus?.charges_enabled === true;
  }

  loadConnectStatus() {
    this.loading = true;
    this.error = null;

    this.paymentService.getConnectStatus(this.company.company_id).subscribe({
      next: (status) => {
        console.log('Stripe Connect status:', status);
        this.connectStatus = status;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load Stripe Connect status:', err);
        // If 404, it means no account exists yet - show not connected state
        if (err.status === 404) {
          this.connectStatus = null;
        } else {
          this.error = this.translateService.instant('companyProfile.billing.loadError');
        }
        this.loading = false;
      }
    });
  }

  startOnboarding() {
    // Check if fee acceptance is required (not already accepted)
    const alreadyAccepted = this.connectStatus?.platform_fee_accepted_at;

    if (!alreadyAccepted) {
      // Show fee acceptance modal first
      const modalRef = this.modalService.open(FeeAcceptanceModal, { size: 'lg', centered: true });
      modalRef.componentInstance.companyName = this.company.company_name;

      modalRef.result.then((accepted) => {
        if (accepted) {
          this.proceedWithOnboarding(true);
        }
      }).catch(() => {
        // Modal dismissed, do nothing
      });
    } else {
      // Fee already accepted, proceed directly
      this.proceedWithOnboarding(false);
    }
  }

  private proceedWithOnboarding(acceptFeeTerms: boolean) {
    this.actionLoading = true;
    this.error = null;

    this.paymentService.createConnectAccount(this.company.company_id, this.selectedBusinessType, acceptFeeTerms).subscribe({
      next: (response) => {
        // Redirect to Stripe onboarding
        window.location.href = response.onboarding_url;
      },
      error: (err) => {
        console.error('Failed to start Stripe onboarding:', err);
        this.error = this.translateService.instant('companyProfile.billing.onboardingError');
        this.actionLoading = false;
      }
    });
  }

  continueOnboarding() {
    this.actionLoading = true;
    this.error = null;

    this.paymentService.refreshOnboarding(this.company.company_id).subscribe({
      next: (response) => {
        window.location.href = response.onboarding_url;
      },
      error: (err) => {
        console.error('Failed to refresh onboarding link:', err);
        this.error = this.translateService.instant('companyProfile.billing.continueError');
        this.actionLoading = false;
      }
    });
  }

  openDashboard() {
    this.actionLoading = true;
    this.error = null;

    this.paymentService.getDashboardLink(this.company.company_id).subscribe({
      next: (response) => {
        window.open(response.dashboard_url, '_blank');
        this.actionLoading = false;
      },
      error: (err) => {
        console.error('Failed to get dashboard link:', err);
        this.error = this.translateService.instant('companyProfile.billing.dashboardError');
        this.actionLoading = false;
      }
    });
  }

  isAdmin(): boolean {
    return this.userRole === 0 || this.userRole === 1;
  }

  getStatusBadgeClass(): string {
    if (!this.connectStatus) return 'badge-light-secondary';

    switch (this.connectStatus.status) {
      case 'active':
        return 'badge-light-success';
      case 'onboarding':
      case 'pending':
        return 'badge-light-warning';
      case 'restricted':
        return 'badge-light-danger';
      default:
        return 'badge-light-secondary';
    }
  }

  getStatusLabel(): string {
    if (!this.connectStatus) return this.translateService.instant('companyProfile.billing.statusNotConnected');

    switch (this.connectStatus.status) {
      case 'active':
        return this.translateService.instant('companyProfile.billing.statusConnected');
      case 'onboarding':
        return this.translateService.instant('companyProfile.billing.statusOnboarding');
      case 'pending':
        return this.translateService.instant('companyProfile.billing.statusPending');
      case 'restricted':
        return this.translateService.instant('companyProfile.billing.statusRestricted');
      default:
        return this.translateService.instant('companyProfile.billing.statusNotConnected');
    }
  }

  getPlatformFeeDisplay(): string {
    if (this.connectStatus?.platform_fee_percentage !== undefined && this.connectStatus?.platform_fee_percentage !== null) {
      return `${(this.connectStatus.platform_fee_percentage * 100).toFixed(1)}%`;
    }
    return 'Default';
  }

  // Service Orders Methods
  loadOrders() {
    this.loadingOrders = true;
    const payload: any = { company_id: this.company.company_id };
    if (this.ordersStatusFilter) {
      payload.status = this.ordersStatusFilter;
    }

    this.endpointService.csr_5(payload).subscribe({
      next: (res) => {
        this.orders = res.data || [];
        this.loadingOrders = false;
      },
      error: () => {
        this.orders = [];
        this.loadingOrders = false;
      }
    });
  }

  onStatusFilterChange() {
    this.loadOrders();
  }

  openCreateInvoiceModal() {
    const modalRef = this.modalService.open(CreateInvoiceModal, { size: 'lg', centered: true });
    modalRef.componentInstance.company = this.company;
    modalRef.result.then((result) => {
      if (result) {
        this.loadOrders();
      }
    }).catch(() => {});
  }

  viewInvoice(order: CompanyServiceOrderResponse) {
    const modalRef = this.modalService.open(ViewInvoiceModal, { size: 'lg', centered: true });
    modalRef.componentInstance.order = order;
  }

  cancelOrder(order: CompanyServiceOrderResponse) {
    if (confirm(this.translateService.instant('companyProfile.billing.cancelConfirm'))) {
      this.endpointService.csr_8(order.order_id).subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('companyProfile.billing.cancelSuccess'));
          this.loadOrders();
        },
        error: (err) => {
          console.error('Error cancelling order:', err);
          this.toastService.error(this.translateService.instant('companyProfile.billing.cancelError'));
        }
      });
    }
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  getOrderStatusClass(status: string): string {
    switch (status) {
      case 'paid':
        return 'badge-light-success';
      case 'pending':
        return 'badge-light-warning';
      case 'cancelled':
      case 'failed':
        return 'badge-light-danger';
      default:
        return 'badge-light-secondary';
    }
  }

  getPaymentTypeLabel(type: string): string {
    return type === 'subscription'
      ? this.translateService.instant('companyProfile.billing.subscription')
      : this.translateService.instant('companyProfile.billing.oneTime');
  }
}
